/**
 * Componente que maneja el envío del survey con reCAPTCHA
 */
import React, { useState } from 'react';
import { useRecaptcha } from '../hooks/useRecaptcha';
import { submitSurvey } from '../services/surveyService';
import SocialShare from './SocialShare';

const SurveySubmitter = ({ 
  answers, 
  otherInputs, 
  isOtherSelected, 
  privacyAccepted, 
  setPrivacyAccepted,
  email,
  onSubmitStart,
  onSubmitSuccess,
  onSubmitError,
  texts,
  language 
}) => {
  const { executeAndValidate } = useRecaptcha();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [hasBeenSubmitted, setHasBeenSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!privacyAccepted || hasBeenSubmitted) {
      if (hasBeenSubmitted) {
        setRecaptchaError('Esta encuesta ya ha sido enviada');
      } else {
        setRecaptchaError('Debes aceptar la política de privacidad');
      }
      return;
    }

    setIsSubmitting(true);
    setRecaptchaError('');
    
    if (onSubmitStart) onSubmitStart();

    try {
      console.log('🚀 Starting survey submission process...');
      
      // Paso 1: Intentar validar reCAPTCHA (no bloqueante)
      let recaptchaResult = null;
      try {
        console.log('🔒 Attempting reCAPTCHA validation...');
        recaptchaResult = await executeAndValidate('submit_survey', 0.5);
        
        if (recaptchaResult.success) {
          console.log('✅ reCAPTCHA validation successful:', recaptchaResult);
        } else {
          console.warn('⚠️ reCAPTCHA validation failed, but continuing...', recaptchaResult);
        }
      } catch (recaptchaError) {
        console.warn('⚠️ reCAPTCHA validation error, but continuing:', recaptchaError);
        recaptchaResult = { success: false, warning: 'reCAPTCHA validation skipped due to error' };
      }

      // Preparar datos del survey
      const finalAnswers = { ...answers };
      
      // Transformar respuestas de preguntas con edad para el backend
      Object.keys(finalAnswers).forEach(questionId => {
        const answer = finalAnswers[questionId];
        // Si es una respuesta combinada con profesión y edad
        if (answer && typeof answer === 'object' && answer.profession && answer.age) {
          // Extraer el valor correcto de la profesión
          let professionValue;
          if (typeof answer.profession === 'string') {
            professionValue = answer.profession;
          } else if (typeof answer.profession === 'object' && answer.profession !== null) {
            // Si la profesión es un objeto, intentar extraer el valor
            professionValue = answer.profession.value || answer.profession.label || JSON.stringify(answer.profession);
          } else {
            professionValue = String(answer.profession);
          }
          
          // Para el backend, enviar profesión y edad separadas por coma
          finalAnswers[questionId] = `${professionValue}, ${answer.age}`;
          console.log(`🔄 Transforming combined answer for question ${questionId}:`, 
                     `{profession: ${JSON.stringify(answer.profession)}, age: ${answer.age}} -> "${professionValue}, ${answer.age}"`);
        }
      });
      
      // Limpiar preguntas de edad del envío (no enviar preguntas auxiliares)
      Object.keys(finalAnswers).forEach(questionId => {
        if (questionId.includes('-edad')) {
          delete finalAnswers[questionId];
          console.log(`🗑️ Removing age question ${questionId} from submission`);
        }
      });
      
      // Agregar valores de "Otro" a las respuestas
      Object.keys(otherInputs).forEach(questionId => {
        if (otherInputs[questionId] && isOtherSelected(questionId)) {
          if (Array.isArray(finalAnswers[questionId])) {
            finalAnswers[questionId] = finalAnswers[questionId].map(answer => {
              if (answer === 'otro' || answer === 'otra' || answer === 'otros' || answer === 'otras') {
                return `${answer}: ${otherInputs[questionId]}`;
              }
              return answer;
            });
          } else {
            if (finalAnswers[questionId] === 'otro' || finalAnswers[questionId] === 'otra' || 
                finalAnswers[questionId] === 'otros' || finalAnswers[questionId] === 'otras') {
              finalAnswers[questionId] = `${finalAnswers[questionId]}: ${otherInputs[questionId]}`;
            }
          }
        }
      });

      // Paso 2: Enviar survey (independiente de reCAPTCHA)
      console.log('📝 Submitting survey data...');
      
      // Agregar el email a finalAnswers
      const surveyData = {
        email: email,
        ...finalAnswers
      };
      
      console.log('📋 Final survey data to submit:', surveyData);
      const result = await submitSurvey(surveyData);
      console.log('✅ Survey submitted successfully:', result);
      
      // Marcar como enviado exitosamente
      setHasBeenSubmitted(true);
      
      if (onSubmitSuccess) onSubmitSuccess({
        survey: result,
        recaptcha: recaptchaResult,
        message: 'Encuesta enviada exitosamente'
      });
      
    } catch (error) {
      console.error('❌ Error al enviar survey:', error);
      const errorMessage = error.message || 'Error al enviar las respuestas. Por favor, inténtalo de nuevo.';
      setRecaptchaError(errorMessage);
      
      if (onSubmitError) onSubmitError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-200 rounded-3xl p-10 space-y-8">
      {/* Mostrar errores de reCAPTCHA */}
      {recaptchaError && (
        <div className="bg-red-100 border-2 border-red-300 rounded-2xl p-4" style={{
          boxShadow: 'inset 6px 6px 12px #d1d5db, inset -6px -6px 12px #ffffff'
        }}>
          <p className="text-red-700 text-lg font-medium">{recaptchaError}</p>
        </div>
      )}
      
      {/* Checkbox de política de privacidad */}
      <div className="flex items-center justify-center space-x-4 bg-gray-200 rounded-2xl p-6 shadow-inner" style={{
        boxShadow: 'inset 8px 8px 16px #bebebe, inset -8px -8px 16px #ffffff'
      }}>
        <input
          type="checkbox"
          id="privacy-policy"
          checked={privacyAccepted}
          onChange={(e) => setPrivacyAccepted(e.target.checked)}
          className="w-6 h-6 rounded-lg shadow-lg transform transition-all duration-200 hover:scale-110"
          style={{
            boxShadow: privacyAccepted 
              ? 'inset 2px 2px 4px #bebebe, inset -2px -2px 4px #ffffff' 
              : '4px 4px 8px #bebebe, -4px -4px 8px #ffffff'
          }}
        />
        <label htmlFor="privacy-policy" className="text-gray-700 text-lg font-medium cursor-pointer hover:text-gray-900 transition-colors duration-200">
          {texts[language].privacyPolicy}
        </label>
      </div>

      {/* Botón de envío */}
      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !privacyAccepted || hasBeenSubmitted}
          className={`
            relative px-16 py-6 rounded-3xl font-bold text-2xl uppercase tracking-wider transform transition-all duration-500
            ${isSubmitting || !privacyAccepted || hasBeenSubmitted
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-gray-200 text-gray-800 hover:scale-105 active:scale-95'
            }
          `}
          style={{
            boxShadow: isSubmitting || !privacyAccepted || hasBeenSubmitted
              ? 'inset 4px 4px 8px #bebebe, inset -4px -4px 8px #ffffff'
              : '12px 12px 24px #bebebe, -12px -12px 24px #ffffff'
          }}
        >
          <div className="relative z-10 flex items-center justify-center space-x-4">
            {isSubmitting ? (
              <>
                <div className="w-8 h-8 border-3 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xl">{texts[language].submitting}</span>
              </>
            ) : hasBeenSubmitted ? (
              <>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-xl">{texts[language].submitted}</span>
              </>
            ) : (
              <>
                <span className="text-xl">{texts[language].submitButton}</span>
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </>
            )}
          </div>
          
          {/* Efecto de energía en el botón */}
          {!isSubmitting && !(!privacyAccepted) && !hasBeenSubmitted && (
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-2xl">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
            </div>
          )}
        </button>
      </div>

      {/* Botón de ir al home - Solo visible cuando se ha enviado */}
      {hasBeenSubmitted && (
        <div className="flex justify-center mt-6">
          <button
            onClick={() => window.location.href = '/'}
            className="group px-12 py-4 rounded-2xl font-bold text-xl tracking-wide transform transition-all duration-300 bg-gray-200 text-gray-800 hover:bg-gradient-to-r hover:from-blue-500 hover:to-purple-600 hover:text-white hover:scale-105 hover:shadow-2xl active:scale-95"
            style={{
              boxShadow: '8px 8px 16px #bebebe, -8px -8px 16px #ffffff'
            }}
            onMouseEnter={(e) => {
              e.target.style.boxShadow = '12px 12px 24px #bebebe, -12px -12px 24px #ffffff, 0 0 20px rgba(59, 130, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = '8px 8px 16px #bebebe, -8px -8px 16px #ffffff';
            }}
          >
            <div className="flex items-center justify-center space-x-3 transition-all duration-300">
              <svg className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="group-hover:scale-105 transition-transform duration-300">{texts[language].goHome}</span>
            </div>
          </button>
        </div>
      )}

      {/* Componente de compartir - Siempre visible */}
      <div className="border-t-2 border-gray-300 pt-6" style={{
        borderImage: 'linear-gradient(90deg, #bebebe, #ffffff, #bebebe) 1'
      }}>
        <SocialShare language={language} />
      </div>
    </div>
  );
};

export default SurveySubmitter;
